import { useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import {
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useChatHistory } from '@/contexts/ChatHistoryContext';
import type { ChatHistoryItem } from '@/types/chatHistory';

const PENDING_NEW_KEY = 'pending-new';

export function ChatSidebar() {
  const {
    chats,
    pendingNewChat,
    currentChatId,
    selectChat,
    selectPendingChat,
    createNewChat,
    deleteChat,
  } = useChatHistory();
  const [chatToDelete, setChatToDelete] = useState<ChatHistoryItem | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirmDelete() {
    if (!chatToDelete || isDeleting) return;
    setIsDeleting(true);
    const ok = await deleteChat(String(chatToDelete.chat_id));
    setIsDeleting(false);
    if (ok) {
      setChatToDelete(null);
    }
  }

  return (
    <>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="New chat" onClick={createNewChat}>
                <PlusIcon />
                <span>New chat</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {pendingNewChat && (
              <SidebarMenuItem key={PENDING_NEW_KEY}>
                <SidebarMenuButton
                  isActive={currentChatId === null}
                  aria-label={pendingNewChat.title || 'Untitled'}
                  onClick={selectPendingChat}
                >
                  <span className="min-w-0 flex-1 truncate text-xs">
                    {pendingNewChat.title || 'Untitled'}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {chats.map((chat) => (
              <SidebarMenuItem key={chat.chat_id}>
                <SidebarMenuButton
                  isActive={String(chat.chat_id) === currentChatId}
                  onClick={() => selectChat(String(chat.chat_id))}
                  title={chat.title}
                >
                  <span className="min-w-0 flex-1 truncate text-xs">
                    {chat.title}
                  </span>
                </SidebarMenuButton>
                <SidebarMenuAction
                  showOnHover
                  aria-label={`Delete ${chat.title}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setChatToDelete(chat);
                  }}
                >
                  <Trash2Icon />
                </SidebarMenuAction>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <AlertDialog
        open={chatToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setChatToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">
                {chatToDelete?.title || 'this chat'}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmDelete();
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
